import apiClient from "src/config/ApiClient";
import { getEngagementsBaseUrl, getTemplateGeneratorBaseUrl } from "src/config/Endpoints";
import i18n from "src/i18n/config";
import { getAuthToken } from "./HttpUtils";
import { getApiLangFromI18n, normalizeApiLang, type ApiLang } from "src/utils/apiLang";

const TEMPLATE_GENERATOR_BASE_URL = getTemplateGeneratorBaseUrl();
const ENGAGEMENTS_BASE_URL = getEngagementsBaseUrl();

export interface EmailLibraryTemplate {
  id: string;
  name: string;
  badge: string;
  description: string;
  campaign_type: string;
  campaign_subtype: string;
  preview_headline: string;
  preview_image_url: string;
}

export interface EmailTemplatesResponse {
  status: string;
  message: string;
  industry: string | null;
  templates: EmailLibraryTemplate[];
  blank_template_id: string;
}

export interface RenderEmailTemplatePayload {
  template_id: string;
  campaign_name?: string;
}

export interface RenderEmailTemplateResponse {
  status: string;
  message: string;
  template_id: string;
  template: unknown;
  html: string;
}

export interface RecommendedProductsPayload {
  audience_names: string[];
  exclude_audience_names?: string[];
  limit?: number;
  campaign_name?: string;
}

export type UnlayerBlock = Record<string, unknown>;

export interface RecommendedProduct {
  product_id: string;
  name: string;
  product_type: string;
  product_handle: string;
  image_url: string;
  product_url: string;
  recommended_to: number;
  share: number;
  avg_rank: number;
  unlayer_column: UnlayerBlock;
  unlayer_row: UnlayerBlock;
}

export interface RecommendedProductsAudience {
  lists: string[];
  excluded_lists: string[];
  missing_lists: string[];
  total_contacts: number;
  unsubscribed_contacts: number;
  matched_customers: number;
}

export interface RecommendedProductsResponse {
  status: "success" | "warning";
  message: string;
  tenant_id: number;
  strategy?: string;
  fallback_reason?: string | null;
  audience: RecommendedProductsAudience;
  generated_at: string;
  products: RecommendedProduct[];
  unlayer_rows: {
    grid_2: UnlayerBlock[];
    grid_3: UnlayerBlock[];
  };
}

export interface RecommendedProductsError {
  status?: number;
  detail?: unknown;
}

const getTenantRequestContext = () => {
  const apiKey = getAuthToken("api_key");
  const tenantId = localStorage.getItem("tenant_id");
  if (!tenantId) {
    throw new Error("Tenant ID not found in localStorage");
  }
  return { apiKey, tenantId };
};

const toApiError = (err: unknown) => {
  const error = err as { response?: { data?: unknown }; message?: string };
  return error.response?.data || error.message;
};

export const fetchEmailTemplates = async (lang?: ApiLang): Promise<EmailTemplatesResponse> => {
  try {
    const { apiKey, tenantId } = getTenantRequestContext();
    const params = new URLSearchParams({
      tenant_id: tenantId,
      api_key: apiKey,
      lang: normalizeApiLang(lang ?? getApiLangFromI18n(i18n)),
    });
    const res = await apiClient.get<EmailTemplatesResponse>(
      `${TEMPLATE_GENERATOR_BASE_URL}/library/templates?${params.toString()}`
    );
    return res.data;
  } catch (err: unknown) {
    throw toApiError(err);
  }
};

export const renderEmailTemplate = async (
  payload: RenderEmailTemplatePayload,
  lang?: ApiLang
): Promise<RenderEmailTemplateResponse> => {
  try {
    const { apiKey, tenantId } = getTenantRequestContext();
    const params = new URLSearchParams({
      api_key: apiKey,
      lang: normalizeApiLang(lang ?? getApiLangFromI18n(i18n)),
    });
    const res = await apiClient.post<RenderEmailTemplateResponse>(
      `${TEMPLATE_GENERATOR_BASE_URL}/library/render?${params.toString()}`,
      { ...payload, tenant_id: Number(tenantId) }
    );
    return res.data;
  } catch (err: unknown) {
    throw toApiError(err);
  }
};

export const fetchRecommendedProducts = async (
  payload: RecommendedProductsPayload,
  lang?: ApiLang
): Promise<RecommendedProductsResponse> => {
  try {
    const { apiKey, tenantId } = getTenantRequestContext();
    const params = new URLSearchParams({
      api_key: apiKey,
      lang: normalizeApiLang(lang ?? getApiLangFromI18n(i18n)),
    });
    const res = await apiClient.post<RecommendedProductsResponse>(
      `${TEMPLATE_GENERATOR_BASE_URL}/library/recommended-products?${params.toString()}`,
      { ...payload, tenant_id: Number(tenantId) }
    );
    return res.data;
  } catch (err: unknown) {
    const error = err as { response?: { status?: number; data?: { detail?: unknown } } };
    const apiError: RecommendedProductsError = {
      status: error.response?.status,
      detail: error.response?.data?.detail,
    };
    throw apiError;
  }
};

interface CampaignPayload {
  tenant_id: number;
  campaign_name: string;
  send_to: string[];
  dont_send_to?: string[];
  html: string;
  send_at: string;
  company_name_engagements?: string;
  subject_line?: string;
  reply_to?: string;
  draft_id?: number;
  lang?: ApiLang;
}

export interface ReplyToResponse {
  tenant_id: number;
  reply_to: string | null;
}

export const fetchReplyTo = async (): Promise<string | null> => {
  try {
    const { apiKey, tenantId } = getTenantRequestContext();
    const res = await apiClient.get<ReplyToResponse>(`${TEMPLATE_GENERATOR_BASE_URL}/reply-to`, {
      params: { tenant_id: tenantId, lang: normalizeApiLang(getApiLangFromI18n(i18n)) },
      headers: { Authorization: `Bearer ${apiKey}` },
    });
    return res.data.reply_to;
  } catch (err: unknown) {
    throw toApiError(err);
  }
};

export interface SendCampaignEmailResponse {
  status: "success" | "warning";
  message: string;
  suppressed_unsubscribed?: number | null;
}

export const sendCampaignEmail = async (
  payload: Omit<CampaignPayload, "tenant_id">
): Promise<SendCampaignEmailResponse> => {
  try {
    const apiKey = getAuthToken("api_key");
    const tenantId = localStorage.getItem("tenant_id");

    if (!tenantId) {throw new Error("Tenant ID not found in localStorage");}
    if (!payload.html?.trim()) {throw new Error("Campaign HTML is required");}

    const { lang: payloadLang, ...payloadWithoutLang } = payload;
    const finalPayload: Omit<CampaignPayload, "lang"> & { tenant_id: number } = {
      ...payloadWithoutLang,
      tenant_id: Number(tenantId),
      company_name_engagements:
        payload.company_name_engagements ??
        localStorage.getItem("company_name_engagements") ??
        localStorage.getItem("company_name") ??
        undefined,
    };

    const params = new URLSearchParams({
      api_key: apiKey,
      lang: normalizeApiLang(payloadLang ?? getApiLangFromI18n(i18n)),
    });
    const url = `${TEMPLATE_GENERATOR_BASE_URL}/send-campaign-email?${params.toString()}`;
    const res = await apiClient.post<SendCampaignEmailResponse>(url, finalPayload, {
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
    });

    return res.data;
  } catch (err: unknown) {
    throw toApiError(err);
  }
};

export interface ResendCampaignPayload {
  previous_campaign_id: string;
  campaign_name: string;
  send_to: string[];
  dont_send_to?: string[];
  html: string;
  send_at: string;
  company_name_engagements?: string;
}

export const resendCampaignEmail = async (payload: ResendCampaignPayload) => {
  try {
    const { apiKey, tenantId } = getTenantRequestContext();
    const res = await apiClient.post(
      `${ENGAGEMENTS_BASE_URL}/resend-campaign-email`,
      { ...payload, tenant_id: tenantId },
      { headers: { Authorization: `Bearer ${apiKey}` } }
    );
    return res.data;
  } catch (err: unknown) {
    throw toApiError(err);
  }
};
